/**
 * Process intake — API client
 *
 * What the department expert knows about how the work actually runs, and the
 * disagreements that surface when their account is compared with the ledger.
 */

import { api } from '../../services/api';

export type AnswerKind = 'text' | 'number' | 'choice' | 'people';

export interface ProcessQuestion {
  key: string;
  prompt: string;
  /** Why the agent is asking. Shown to the expert — people answer better when they know. */
  why: string;
  kind: AnswerKind;
  choices?: string[];
  required: boolean;
  checkAgainst?: {
    metricKey: string;
    tolerancePercent: number;
    describes: string;
  };
}

export interface ProcessAnswer {
  questionKey: string;
  value: string | number | string[];
  answeredBy: string;
  answeredAt: string;
}

export interface QuestionSetView {
  key: string;
  label: string;
  playbookKey: string | null;
  agentKey: string;
  intro: string;
  questions: ProcessQuestion[];
  /** Answers already given, if this set has been filled in before. */
  answers: ProcessAnswer[];
  completedAt: string | null;
  ownerLabel: string | null;
  reviewAfter: string | null;
  /** True when an open contradiction is stopping this set's playbook. */
  blocked: boolean;
}

export interface Contradiction {
  id: string;
  profileId: string;
  questionKey: string;
  playbookKey: string | null;
  statedValue: number;
  statedBy: string;
  statedAt: string;
  metricKey: string;
  measuredValue: number;
  measuredAt: string;
  differencePercent: number;
  status: 'open' | 'data_accepted' | 'explained' | 'dismissed';
  resolutionNote: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
}

export interface PlaybookBlock {
  playbookKey: string;
  reason: 'open_contradiction' | 'intake_incomplete';
  message: string;
  waitingOn: string;
  contradictionIds: string[];
}

export interface SubmitResult {
  profile: { id: string; questionSetKey: string; completedAt: string | null };
  contradictions: Contradiction[];
  /** Required questions left blank. Reported, not refused. */
  missingRequired: string[];
}

export type ResolutionOutcome = 'data_accepted' | 'explained' | 'dismissed';

interface Envelope<T> {
  success: boolean;
  data: T;
}

class ProcessService {
  async listQuestionSets(): Promise<{ sets: QuestionSetView[]; blocks: PlaybookBlock[] }> {
    const response = await api.get<Envelope<{ sets: QuestionSetView[]; blocks: PlaybookBlock[] }>>(
      '/core/process/questions'
    );
    return response.data ?? { sets: [], blocks: [] };
  }

  /**
   * Submit an intake.
   *
   * Blank required answers come back in `missingRequired` rather than failing:
   * a partial account is better than none. The playbook stays blocked until
   * they are filled in.
   */
  async submit(
    setKey: string,
    answers: Array<{ questionKey: string; value: string | number | string[] }>,
    ownerLabel: string
  ): Promise<SubmitResult> {
    const response = await api.post<Envelope<SubmitResult>>(
      `/core/process/questions/${setKey}`,
      { answers, ownerLabel }
    );
    return response.data;
  }

  async listContradictions(): Promise<{ open: Contradiction[]; blocks: PlaybookBlock[] }> {
    const response = await api.get<Envelope<{ open: Contradiction[]; blocks: PlaybookBlock[] }>>(
      '/core/process/contradictions'
    );
    return response.data ?? { open: [], blocks: [] };
  }

  /**
   * Settle a disagreement. The note is required — it is the part the agent
   * keeps, and closing one without it destroys the finding.
   */
  async resolve(
    contradictionId: string,
    outcome: ResolutionOutcome,
    note: string
  ): Promise<Contradiction> {
    const response = await api.post<Envelope<Contradiction>>(
      `/core/process/contradictions/${contradictionId}/resolve`,
      { outcome, note }
    );
    return response.data;
  }
}

export const processService = new ProcessService();
